/*
 * Purpose: Design style controls — surface vs routines (theme lives in workspace menu)
 * Last modified: 2026-07-10 | Agent
 * Completeness: 92
 */
import { Layers, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDesignStyle } from '@/components/design-style-provider';
import { designStyleLabels } from '@/lib/design-tokens/routines';
import { cn } from '@/lib/utils';

interface DesignStyleToggleProps {
  variant?: 'inline' | 'fixed';
  className?: string;
}

export function DesignStyleToggle({ variant = 'inline', className }: DesignStyleToggleProps) {
  const { designStyle, setDesignStyle } = useDesignStyle();

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        variant === 'fixed' &&
          'fixed right-4 z-40 rounded-lg border border-border/60 bg-background/95 p-1 shadow-md backdrop-blur-sm',
        variant === 'fixed' && 'top-[var(--app-top-offset,0.75rem)]',
        className
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 bg-background/95 shadow-sm"
          >
            {designStyle === 'routines' ? (
              <Layers className="h-4 w-4" />
            ) : (
              <Palette className="h-4 w-4" />
            )}
            <span className="hidden text-xs font-medium sm:inline">
              {designStyleLabels[designStyle]}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Design style</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => setDesignStyle('surface')}
            className={designStyle === 'surface' ? 'bg-accent' : undefined}
          >
            <Palette className="mr-2 h-4 w-4" />
            Surface — warm planes
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDesignStyle('routines')}
            className={designStyle === 'routines' ? 'bg-accent' : undefined}
          >
            <Layers className="mr-2 h-4 w-4" />
            Routines — autoblocks.ai
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
